import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
  selectMFControlPanelObservedDataLastConsulted,
} from 'redux/reducers/meteorologyFieldsObservedDataControlPanelReducer';
import {
  selectMFControlPanelLastConsulted,
} from 'redux/reducers/meteorologyFieldsControlPanelReducer';
import { useGetObservedDataOptionsQuery } from 'services/api/Meteorology/Fields/ObservedData';
import useControlPanelOptions from 'utils/hooks/meteorology/useControlPanelOptions';

const VIEW_MODE_LABEL: Record<string, string> = {
  spatial: 'Espacial',
  heatmap: 'Heatmap',
};

const TAB_LABEL = {
  observedData: 'Dados Observados',
  numericForecast: 'Previsão Numérica do Tempo',
} as const;

export type DisplayTitleTab = keyof typeof TAB_LABEL;

export default function DisplayTitle({ tab }: { tab: DisplayTitleTab }) {
  const isObserved = tab === 'observedData';

  const observedSnapshot = useSelector(selectMFControlPanelObservedDataLastConsulted);
  const pntSnapshot = useSelector(selectMFControlPanelLastConsulted);

  const { data: observedOptions } = useGetObservedDataOptionsQuery(
    undefined,
    { skip: !isObserved },
  );
  const { variables: pntVariables } = useControlPanelOptions('pnt');

  const variableId = isObserved
    ? observedSnapshot?.variable
    : pntSnapshot?.variable;
  const periodId = isObserved
    ? observedSnapshot?.selectedPeriod
    : pntSnapshot?.period;
  const viewMode = isObserved ? observedSnapshot?.viewMode : pntSnapshot?.viewMode;

  const variableLabel = useMemo(() => {
    if (!variableId) return undefined;
    if (isObserved) {
      const found = observedOptions?.variables?.find(
        (v) => v.id === (variableId as unknown as string),
      );
      return found?.label;
    }
    const found = pntVariables.find((v) => v.id === (variableId as unknown as string));
    return found?.label;
  }, [isObserved, observedOptions, pntVariables, variableId]);

  const periodLabel = useMemo(() => {
    if (!periodId) return undefined;
    if (!isObserved) return periodId;
    if (!variableId) return undefined;
    const found = observedOptions?.variables
      ?.find((v) => v.id === (variableId as unknown as string))
      ?.predefinedPeriods.find((p) => p.id === periodId);
    return found?.label;
  }, [isObserved, observedOptions, variableId, periodId]);

  if (!viewMode) return null;

  // PNT nao inclui o periodo no titulo (o slider/figura ja mostram p+N);
  // Dados Observados mantem o periodo predefinido (ex: "neste mes").
  const parts = isObserved
    ? [TAB_LABEL[tab], VIEW_MODE_LABEL[viewMode], variableLabel, periodLabel]
    : [TAB_LABEL[tab], VIEW_MODE_LABEL[viewMode], variableLabel];

  const visibleParts = parts.filter(Boolean);

  if (visibleParts.length <= 1) return null;

  return (
    <h2 className="text-primary fs-5 fw-bold text-center mt-3 mb-2">
      {visibleParts.join(' - ')}
    </h2>
  );
}
